import {formatSlaDuration} from '../../tickets/utils/slaPresentation';
import type {TicketGroupStats} from './groupTickets';

/**
 * How a group's subtotals read in its header.
 *
 * Deliberately the same treatment the table's totals row gives the same three
 * rollups — a group header is a totals row scoped to one bucket, so a reader
 * comparing "42% resolved" in a header against the totals row above it must be
 * seeing the same rounding and the same duration units. Durations therefore go
 * through `formatSlaDuration`, the seconds-based formatter the rest of the
 * ticketing tree already uses, rather than a bespoke one.
 *
 * FOR-73 brings a `totalsFormatting` module for the totals row itself; these two
 * should collapse into it once that lands, rather than staying as two formatters
 * that agree only by coincidence.
 */

/** An absent subtotal — no rows to compute it from. Matches the table's empty-cell dash. */
const NO_VALUE = '—';

export function formatResolvedRatio(ratio: number | null): string {
	if (ratio == null) return NO_VALUE;
	return `${Math.round(ratio * 100)}%`;
}

export function formatMedianResolution(seconds: number | null): string {
	if (seconds == null) return NO_VALUE;
	return formatSlaDuration(seconds);
}

export function formatGroupCount(count: number): string {
	return `${count} ${count === 1 ? 'ticket' : 'tickets'}`;
}

/** The three subtotals PRD §6.7 puts on a group header, in reading order. */
export function groupSubtotals(stats: TicketGroupStats): {count: string; resolved: string; median: string} {
	return {
		count: formatGroupCount(stats.count),
		resolved: formatResolvedRatio(stats.resolvedRatio),
		median: formatMedianResolution(stats.medianResolutionSeconds),
	};
}
